import { FC, MutableRefObject, useEffect, useRef, useState, useCallback, useMemo } from 'react';
// LEAFLET'S CORE CSS MUST BE IMPORTED HERE, IN THIS CHUNK. The dead-dependency
// cleanup removed mapbox-gl.css (correctly - zero .mapboxgl-* selectors match
// a Leaflet DOM) but left a comment claiming Leaflet's own CSS "reaches this
// page via the shared map module". That was only ever true of the monolithic
// bundle: since route-level code splitting, Location.tsx's
// leaflet/dist/leaflet.css import lands in the SETTINGS chunks, and
// /market-place loaded no Leaflet core CSS at all - .leaflet-tile lost
// position:absolute, so every tile stacked in normal flow and the live map
// rendered as one lone tile. Container pixel-diffs never caught it because
// tiles cannot load in the sandbox (no network), which made the broken map
// and the working map look identical. Each chunk that mounts a Leaflet map
// must import this file itself; webpack dedupes it at runtime.
import 'leaflet/dist/leaflet.css';
import './map.css';
import clsx from 'clsx';
import { useAppSelector } from '../../../lib/redux/hooks';
import { useGetMarketPlaceMapData } from '../../../hooks/marketPlace/useGetMarketPlaceMapData';
import { ApiSearchOrderEnum } from '../../../util/enum/enum';
import { AuthUserState } from '../../../context/AuthUserProvider';
import wantedIcon from '../../../assets/wanted-marker.svg';
import personalIcon from '../../../assets/personal-marker.svg';
import storeFrontIcon from '../../../assets/storefront-marker.svg';
import eventIcon from '../../../assets/event-marker.svg';
import communityMarkerIcon from '../../../assets/community-marker.svg';
import onlineMarkerIcon from '../../../assets/online-marker.svg';
import PopupSlider from './PopupSlider';
import SellerStackPopup from './SellerStackPopup';
import SheetListingCard from './SheetListingCard';
import ShopPopupCard from './ShopPopupCard';
import SpotMomentumChip from './SpotMomentumChip';
import CategoryChipBar from '../../../components/shared/CategoryChipBar/CategoryChipBar';
import { useDispatch } from 'react-redux';
import {
  addFilter,
  addFilterModal,
  addLatLng,
  setAutoWidened,
  setHoveredListing,
  setMovedAway
} from '../../../lib/redux/reducers/filter.slice';

// Hover sync applies only where hover exists (map plan F4).
const canHover = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;
import { setProfileData, showSlide } from '../../../lib/redux/reducers/slide.slice';
import {
  MapContainer,
  Marker,
  Popup,
  Rectangle,
  TileLayer,
  ZoomControl,
  useMap
} from 'react-leaflet';
import * as L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { debounce } from 'lodash';
import { useGetUsersOnMap } from '../../../hooks/marketPlace/useGetUsersOnMap';
import { useGetOnlineStorefronts } from '../../../hooks/marketPlace/useGetOnlineStorefronts';
import { MAP_MARKER_LIMIT } from '../../../hooks/marketPlace/useGetMarketPlaceMapData';
import { getFounderConfig } from '../../../util/founderTier';
import useDeviceDetect from '../../../hooks/useDeviceDetect';
import { FormattedMessage, useIntl } from 'react-intl';
import { TRANSLATED_CONSTANTS } from '../../../static/translatedConstants';
import { CLICKED_ZOOM } from '../../../constants/data';
import { buildTileUrl } from '../../../util/mapTiles';
import { useTheme } from '../../../context/ThemeProvider';
import MapNavigationPointIcon from '../../../components/icons/MapNavigationPointIcon';
import SearchIcon from '../../../components/icons/SearchIcon';

type Props = {
  className?: string;
  width?: string;
  height?: string;
  isMobile?: boolean;
  // F2: full-screen mobile map renders pin taps as a bottom sheet instead of
  // Leaflet popups.
  sheetMode?: boolean;
};

// Unified marker family (map plan E2): the SVGs now carry a bottom tail, so
// the ANCHOR is the tail tip (the exact coordinate) and popups open above the
// badge. divIcon-with-inner-img instead of L.icon so hover/selected effects
// can transform the INNER element - a CSS transform on the marker element
// itself would fight Leaflet's inline translate3d positioning and teleport
// pins to the map corner. The type lives in the wrapper className; the
// cluster icon builder reads it from there.
const createTypePin = (svgUrl: string, type: string) =>
  L.divIcon({
    html: `<div class="dl-pin"><img src="${svgUrl}" alt="" draggable="false" /></div>`,
    className: `dl-pin-wrap dl-pin-${type}`,
    iconSize: [38, 47],
    iconAnchor: [19, 47],
    popupAnchor: [0, -41]
  });
const listingIcon = createTypePin(personalIcon, 'personal');
const storeIcon = createTypePin(storeFrontIcon, 'storefront');
const needIcon = createTypePin(wantedIcon, 'wanted');
const eventMarkerIcon = createTypePin(eventIcon, 'event');
// Online Continent: online-only shops use a tailless 38x38 globe pin (no tail -
// the point is "everywhere", not a specific coordinate).
const onlineIcon = L.divIcon({
  html: `<div class="dl-pin"><img src="${onlineMarkerIcon}" alt="" draggable="false" /></div>`,
  className: 'dl-pin-wrap dl-pin-online',
  iconSize: [38, 38],
  iconAnchor: [19, 19],
  popupAnchor: [0, -23]
});
// Deterministic Antarctica scatter: online-only shops have no real location, so
// each sits at a fixed point in the Antarctic band seeded by its id (pins never
// stack and stay put across loads).
const onlineShopLatLng = (id: any): [number, number] => {
  const n = Number(id) || 0;
  const a = Math.abs((Math.sin(n * 12.9898) * 43758.5453) % 1);
  const b = Math.abs((Math.sin(n * 78.233) * 24634.6345) % 1);
  return [-63 - a * 18, -165 + b * 330];
};
// The Online Continent zone: a stylized band over the Antarctic that frames the
// online-only pins (covers onlineShopLatLng's scatter range). Leaflet bounds are
// [[south, west], [north, east]] in [lat, lng].
const ANTARCTICA_BOUNDS: [[number, number], [number, number]] = [
  [-83, -170],
  [-61, 170]
];
const ANTARCTICA_LABEL_POS: [number, number] = [-61, 0];
// Seed the Map (Doge Spotters): community-reported, still-unclaimed pins get the
// dedicated Direction B "Trust Teal" marker (teal #0E9384 head + verified check),
// distinct from claimed / owner-created storefront shops. The dl-pin-community
// wrapper class stays for cluster-dot detection; the teal lives in the art now,
// not a CSS hue-rotate.
const communityIcon = createTypePin(communityMarkerIcon, 'community');
// A storefront pin is a community/unclaimed spot exactly when its claimStatus is
// 'unclaimed' (set by the approve flow). Claimed/owner-created shops are normal.
const isCommunitySpot = (entry: any) => entry?.claimStatus === 'unclaimed';

const createClusterCustomIcon = function (cluster: L.MarkerCluster) {
  let hasPersonal = false;
  let hasWanted = false;
  let hasStorefront = false;
  let hasEvent = false;
  let hasCommunity = false;

  const markers = cluster.getAllChildMarkers();
  for (const marker of markers) {
    // E2: type pins are divIcons - the type is in the className. The iconUrl
    // fallback keeps this working if any L.icon marker ever joins the group.
    const iconOptions: any = marker?.options.icon?.options ?? {};
    const typeKey = `${iconOptions.className || ''} ${iconOptions.iconUrl || ''}`;
    if (!typeKey.trim()) continue;
    if (typeKey.includes('personal')) hasPersonal = true;
    else if (typeKey.includes('community')) hasCommunity = true;
    else if (typeKey.includes('storefront')) hasStorefront = true;
    else if (typeKey.includes('wanted')) hasWanted = true;
    else if (typeKey.includes('event')) hasEvent = true;
  }

  const childCount = cluster.getChildCount();

  // Stable type order so dot positions don't shuffle: personal, wanted, event, storefront.
  const dots = [
    hasPersonal && '<span class="cluster-dot cluster-dot-personal"></span>',
    hasWanted && '<span class="cluster-dot cluster-dot-wanted"></span>',
    hasEvent && '<span class="cluster-dot cluster-dot-event"></span>',
    hasCommunity && '<span class="cluster-dot cluster-dot-community"></span>',
    hasStorefront && '<span class="cluster-dot cluster-dot-storefront"></span>'
  ]
    .filter(Boolean)
    .join('');

  return L.divIcon({
    html: `<div class="cluster-cont">
      <div class="cluster-badge">${childCount}</div>
      <div class="cluster-dots">${dots}</div>
    </div>`,
    className: 'custom-marker-cluster',
    iconSize: L.point(48, 60, true)
  });
};
// HTML-escape user-supplied strings before they go into a Leaflet divIcon.
const esc = (s: any) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => {
    const map: any = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return map[c];
  });

// Avatar-chip pin for an opted-in user: round gold-ringed avatar, plus a small
// crown for Founders (first 500 ids). Inline styles so no extra CSS is needed.
const userPinHtml = (user: any) => {
  const founder = getFounderConfig(user?.id);
  const initial = esc((user?.name || user?.username || '?').charAt(0).toUpperCase());
  const crown = founder
    ? '<svg width="16" height="11" viewBox="0 0 24 16" style="position:absolute;top:-8px;left:50%;transform:translateX(-50%)"><path d="M2 14 L4 3 L9 8 L12 1 L15 8 L20 3 L22 14 Z" fill="#EF9F27" stroke="#9A6A12" stroke-width="1"/></svg>'
    : '';
  const inner = user?.avatarUrl
    ? `<img src="${esc(user.avatarUrl)}" style="width:100%;height:100%;object-fit:cover" />`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#FAEEDA;color:#9A6A12;font-weight:700;font-size:14px">${initial}</div>`;
  return `<div style="position:relative;width:32px;height:32px"><div style="width:32px;height:32px;border-radius:50%;overflow:hidden;border:3px solid #EF9F27;background:#fff;box-sizing:border-box;box-shadow:0 1px 3px rgba(0,0,0,.35)">${inner}</div>${crown}</div>`;
};

const createUserIcon = (user: any) =>
  L.divIcon({
    html: userPinHtml(user),
    className: 'user-map-pin',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });

const createUserClusterIcon = (cluster: L.MarkerCluster) => {
  const count = cluster.getChildCount();
  return L.divIcon({
    html: `<div style="width:34px;height:34px;border-radius:50%;background:#EF9F27;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:#3b2a00;font-weight:700;font-size:13px">${count}</div>`,
    className: 'user-cluster-pin',
    iconSize: L.point(34, 34, true)
  });
};

// Defined at module scope (not inside MapboxLe) so that React keeps a single
// stable component instance across parent re-renders. When this was declared
// inside MapboxLe, every parent render created a brand-new component reference,
// React unmounted/remounted it, and Leaflet event listeners were torn off and
// re-bound constantly — a contributor to the _leaflet_pos race during zoom
// transitions and the cause of the earlier infinite-loop bug.
type MapEventComponentProps = {
  handleMapMove: () => void;
  hasSyncedInitialBounds: MutableRefObject<boolean>;
};

const MapEventComponent: FC<MapEventComponentProps> = ({
  handleMapMove,
  hasSyncedInitialBounds
}) => {
  const map = useMap();

  useEffect(() => {
    // Debounce the moveend handler so a continuous drag/zoom or animated
    // fitBounds doesn't fire several dispatches back-to-back. 200ms is small
    // enough that the UI still feels responsive after the user lets go.
    const debouncedMove = debounce(handleMapMove, 200);
    map.on('moveend', debouncedMove);

    // Run once immediately (not debounced) so the initial query syncs to the
    // visible viewport without a delay on first page load.
    if (!hasSyncedInitialBounds.current) {
      hasSyncedInitialBounds.current = true;
      handleMapMove();
    }

    return () => {
      map.off('moveend', debouncedMove);
      debouncedMove.cancel();
    };
  }, [map, handleMapMove, hasSyncedInitialBounds]);

  return null;
};

// Cancels any in-flight Leaflet pan/zoom animation the instant the map unmounts.
// react-leaflet removes the map (and clears its panes) on unmount; if a deferred
// panBy() from drag inertia, an animated fitBounds, or a popup auto-pan is still
// queued, it runs against an undefined _mapPane and throws
// "undefined is not an object (evaluating 'el.classList')". Stopping first avoids
// it. This child's cleanup runs before react-leaflet's own map.remove().
const MapCleanup: FC = () => {
  const map = useMap();
  useEffect(() => {
    return () => {
      try {
        map.stop();
      } catch {
        // Map is already gone — nothing to stop.
      }
    };
  }, [map]);
  return null;
};

// Closes the F2 bottom sheet when the user taps the map itself.
const SheetMapClickClose: FC<{ onMapClick: () => void }> = ({ onMapClick }) => {
  const map = useMap();
  useEffect(() => {
    map.on('click', onMapClick);
    return () => {
      map.off('click', onMapClick);
    };
  }, [map, onMapClick]);
  return null;
};

// Wheel-zoom guard (map plan A5): in the stacked band layouts (<1024px) the
// mouse wheel over the map hijacked PAGE scrolling. Toggled through Leaflet's
// handler because MapContainer's scrollWheelZoom prop is immutable after
// mount, and the viewport class can change when the window is resized.
const WheelZoomGuard: FC<{ enabled: boolean }> = ({ enabled }) => {
  const map = useMap();
  useEffect(() => {
    if (enabled) {
      map.scrollWheelZoom.enable();
    } else {
      map.scrollWheelZoom.disable();
    }
  }, [map, enabled]);
  return null;
};

// The one world the map may show: [[south, west], [north, east]] in [lat, lng].
// Shared by MapContainer's maxBounds and the TileLayer's tile bounds.
const WORLD_BOUNDS: [[number, number], [number, number]] = [
  [-85, -180],
  [85, 180]
];
const BASE_MIN_ZOOM = 2;

// Big-screen fix (ultrawide monitors). At zoom 2 the whole world is only
// ~1024px across, so a large map panel can be wider (and on a 1440p screen
// taller) than the world itself. Leaflet then floats the world in the middle
// with grey bars, slides it back to center after every drag, and the tile
// server fills the empty side with stray strips of repeated map. Raising the
// minimum zoom to the first level whose world covers the whole panel keeps
// the map edge to edge on any screen. Re-checked on every resize; phones,
// tablets and laptops still compute 2, so nothing changes for them.
const FitWorldMinZoom: FC = () => {
  const map = useMap();
  useEffect(() => {
    const applyMinZoom = () => {
      const size = map.getSize();
      if (!size.x || !size.y) {
        return;
      }
      const world = L.latLngBounds(WORLD_BOUNDS);
      const nw = map.project(world.getNorthWest(), 0);
      const se = map.project(world.getSouthEast(), 0);
      const scale = Math.max(size.x / (se.x - nw.x), size.y / (se.y - nw.y));
      // The epsilon keeps an exact fit from rounding up a whole level.
      const minZoom = Math.max(BASE_MIN_ZOOM, Math.ceil(Math.log2(scale) - 1e-6));
      // Jump (no animation) so the first viewport sync reads the final view.
      if (map.getZoom() < minZoom) {
        map.setZoom(minZoom, { animate: false });
      }
      map.setMinZoom(minZoom);
    };
    applyMinZoom();
    map.on('resize', applyMinZoom);
    return () => {
      map.off('resize', applyMinZoom);
    };
  }, [map]);
  return null;
};

const MapboxLe: FC<Props> = ({ className, width, height, isMobile = false, sheetMode = false }) => {
  const { authUser } = AuthUserState();
  const { theme } = useTheme();
  const intl = useIntl();
  // Wheel zoom only in the desktop split view; band layouts keep page scroll.
  const { isDesktop: isDesktopViewport } = useDeviceDetect();
  const mapRef = useRef<any>(null);
  const dispatch = useDispatch();
  const [isFlyto, setIsFlyto] = useState<boolean>(false);
  const filterFromModal = useAppSelector((state) => state.filter.filterModal);
  // Bookkeeping only (never rendered) - a ref instead of state so pans don't
  // schedule extra re-renders (map plan A4).
  const lastBoundsRef = useRef<any>(null);
  const latLng = useAppSelector((state) => state.filter.latLng);
  // Map controls (plan B5). autoSearch persists across visits; hasPendingArea
  // arms the "Search this area" pill while the toggle is off; the force ref
  // makes programmatic moves (B2 auto-widen) search regardless of the toggle.
  const [autoSearch, setAutoSearch] = useState<boolean>(() => {
    try {
      return localStorage.getItem('mapAutoSearch') !== 'off';
    } catch {
      return true;
    }
  });
  const [hasPendingArea, setHasPendingArea] = useState(false);
  const [locationDenied, setLocationDenied] = useState(false);
  const forceNextSearchRef = useRef(false);
  // Tracks whether we've already synced the query to the map viewport on mount.
  // Passed down to MapEventComponent so its on-mount effect only fires the
  // initial dispatch once, even if its useEffect re-runs because handleMapMove
  // (which is in its dep array) gets a new identity from useCallback.
  const hasSyncedInitialBounds = useRef(false);

  // Initial center/zoom only: react-leaflet v4 MapContainer ignores changes
  // to these props after mount, so this is deliberately not state (plan A4).
  const viewport = {
    center: [latLng?.lat, latLng.lng] as [number, number],
    zoom: latLng?.zoom // CLICKED_ZOOM
  };

  // Fly ONLY on explicit request (map plan C2): location pick, Search, or
  // reset bump flyRequestId. Previously this was keyed on the address string,
  // which stopped working once pans no longer rewrite the address (re-picking
  // the same city wouldn't change the string, so the map wouldn't fly).
  const flyRequestId = useAppSelector((state) => state.filter.flyRequestId);
  useEffect(() => {
    if (flyRequestId === 0) {
      // Initial mount - the map already starts at the stored latLng viewport.
      return;
    }
    if (mapRef.current) {
      const locBounds = filterFromModal.locationBounds;
      setIsFlyto(true);
      // An explicit pick/search supersedes any armed "Search this area" pill.
      setHasPendingArea(false);
      if (
        locBounds &&
        Number.isFinite(locBounds.minLat) &&
        Number.isFinite(locBounds.maxLat) &&
        Number.isFinite(locBounds.minLng) &&
        Number.isFinite(locBounds.maxLng)
      ) {
        const maxLat = locBounds.maxLat;
        const maxLng = locBounds.maxLng;
        const minLat = locBounds.minLat;
        const minLng = locBounds.minLng;
        const leafletBounds = L.latLngBounds(L.latLng(minLat, minLng), L.latLng(maxLat, maxLng));
        mapRef.current.fitBounds(leafletBounds, {
          duration: 1500 / 1000 // Duration in seconds
        });
      }
    }
    // The effect body reads the freshest filterFromModal from this render;
    // it must only RUN when the fly id changes.
    // eslint-disable-next-line
  }, [flyRequestId]);

  const { geoData, loading } = useGetMarketPlaceMapData(
    filterFromModal?.moreFilter,
    filterFromModal.categories,
    filterFromModal.searchText,
    filterFromModal.locationBounds,
    0,
    0,
    ApiSearchOrderEnum.PUBLISHED_DESC,
    authUser?.id || null
  );

  const { users } = useGetUsersOnMap(filterFromModal.locationBounds);
  const { onlineStorefronts } = useGetOnlineStorefronts();
  const continentLabelIcon = useMemo(
    () =>
      L.divIcon({
        className: 'online-continent-wrap',
        html:
          '<div class="online-continent-label">' +
          intl.formatMessage(TRANSLATED_CONSTANTS.ONLINE_CONTINENT_LABEL) +
          '</div><div class="online-continent-pill">' +
          intl.formatMessage(TRANSLATED_CONSTANTS.ONLINE_CONTINENT_LEGEND) +
          '</div>',
        iconSize: [260, 68],
        iconAnchor: [130, 8]
      }),
    [intl]
  );

  // Memoized so the array reference is stable when geoData hasn't changed.
  // Previously this was useState+useEffect, which produced a fresh array on
  // every render and made MarkerClusterGroup tear down & rebuild every marker
  // — the visible "pin blink" during pans, and a contributor to the
  // _leaflet_pos race during zoom transitions.
  const points = useMemo(() => {
    if (!geoData) return [];
    const useableData = JSON.parse(geoData);
    return (
      useableData?.features?.map((listing: any) => ({
        type: 'Feature',
        properties: {
          cluster: false,
          listingId: listing.id,
          category: listing.category,
          data: listing.properties
        },
        geometry: {
          type: 'Point',
          coordinates: listing.geometry.coordinates
        }
      })) ?? []
    );
  }, [geoData]);

  // One-shot zero-results fallback (map plan B2): visitors who grant
  // geolocation land at city zoom, and if their town has no listings the
  // marketplace looks dead. When the FIRST fetch comes back empty (and no
  // keyword search is active), widen once to world view so the clusters that
  // do exist are visible; the moveend that follows refetches world bounds.
  // Evaluated exactly once per page load, so later pans into empty areas
  // never yank the map away from the user.
  const initialEmptyCheckDone = useRef(false);
  // F5: a restored view (?bbox / last visit) is deliberate - never widen it.
  const viewRestored = useAppSelector((state) => state.filter.viewRestored);
  useEffect(() => {
    if (loading || initialEmptyCheckDone.current || !hasSyncedInitialBounds.current) {
      return;
    }
    initialEmptyCheckDone.current = true;
    if (points.length === 0 && !filterFromModal?.searchText && !viewRestored) {
      dispatch(setAutoWidened(true));
      // Widening only helps if the wide view actually refetches - force the
      // next moveend to search even when "Search as I move" is off (B5).
      forceNextSearchRef.current = true;
      mapRef.current?.setView([20, 0], 2, { animate: true });
    }
    // Extra deps are harmless: the ref above makes any re-run a no-op.
  }, [loading, points, filterFromModal?.searchText, viewRestored, dispatch]);

  // Shared "search these bounds" dispatch (map plan B5) - used by the
  // auto-search path in handleMapMove AND the manual "Search this area" pill,
  // so the clamp/bookkeeping logic can't drift between them.
  const dispatchAreaSearch = useCallback(
    (leafletMap: any) => {
      const bounds = leafletMap.getBounds();
      if (!bounds.isValid()) {
        return;
      }
      const center = leafletMap.getCenter();
      // F7 fix: store the map's REAL zoom (the old latLng.zoom carry-over
      // left the URL's z= stale after fitBounds flights).
      dispatch(
        addLatLng({ latLng: { lat: center.lat, lng: center.lng, zoom: leafletMap.getZoom() } })
      );
      // Keep the user's chosen location LABEL through pans (map plan C2):
      // only the query bounds move with the map. The old behavior stamped
      // 'Current View' over the address on every pan, which corrupted the
      // location field (typing appended to "Current View") and made the
      // headline lie. The movedAway flag lets the headline say "in this
      // area" while the field keeps saying e.g. "Miami, Florida".
      dispatch(setMovedAway(true));
      dispatch(
        addFilterModal({
          filterModal: {
            ...filterFromModal,
            locationBounds: {
              // Clamp to a single world. Leaflet longitudes can run past +/-180
              // when panning across the antimeridian into a duplicate world copy;
              // listings are stored in [-180, 180], so unclamped bounds match
              // nothing and that side of the map comes up empty.
              maxLat: Math.min(90, bounds.getNorthEast().lat),
              maxLng: Math.min(180, bounds.getNorthEast().lng),
              minLat: Math.max(-90, bounds.getSouthWest().lat),
              minLng: Math.max(-180, bounds.getSouthWest().lng)
            }
          }
        })
      );
      lastBoundsRef.current = bounds;
      setHasPendingArea(false);
    },
    [filterFromModal, latLng, dispatch]
  );

  // Floating keyword search (F7 round 4, Luke: "much harder to search the
  // map for a keyword now"). The full-screen mobile map view css-hides the
  // whole Filters block, so keyword entry required: toggle to list, expand
  // the collapsed F3 input, search, toggle back. This bar searches the
  // CURRENT view in place: same full-spread filterModal dispatch as
  // dispatchAreaSearch, keyword only - no fly, no location/label rewrite,
  // no movedAway change. The map query takes searchText directly, so pins
  // and the "Show N listings" pill count update where the user is looking.
  const [mapSearchText, setMapSearchText] = useState(filterFromModal?.searchText ?? '');
  useEffect(() => {
    // Re-seed when the keyword changes elsewhere (list-side search/Clear,
    // F5 restore) so the bar never shows a stale term.
    setMapSearchText(filterFromModal?.searchText ?? '');
  }, [filterFromModal?.searchText]);
  const applyMapSearch = useCallback(
    (value: string) => {
      // Mirror Filters.handleSearch's two searchText dispatches so list and
      // map stay coherent when the user toggles views. addFilterModal
      // REPLACES the modal - the spread is mandatory (documented trap).
      dispatch(addFilter({ searchText: value }));
      dispatch(addFilterModal({ filterModal: { ...filterFromModal, searchText: value } }));
    },
    [dispatch, filterFromModal]
  );

  const handleMapMove = useCallback(async () => {
    const isPopupOpen = document.getElementsByClassName('leaflet-popup')?.length > 0;
    if (mapRef.current && !isFlyto && !isPopupOpen) {
      const leafletMap = mapRef.current;
      const bounds = leafletMap.getBounds();
      if (bounds.isValid()) {
        // Perf (map plan A4): no setViewport here. react-leaflet v4
        // MapContainer props are immutable after mount, so pushing the
        // center/zoom back into state re-rendered the whole component on
        // every pan for zero visual effect. lastBounds lives in a ref for
        // the same reason - it is bookkeeping, not render input.
        const lastBounds = lastBoundsRef.current;
        const boundsChanged =
          lastBounds === null ||
          bounds.getNorthEast().distanceTo(lastBounds?.getNorthEast()) > 2200 ||
          bounds.getSouthWest().distanceTo(lastBounds?.getSouthWest()) > 2200;

        if (boundsChanged) {
          // "Search as I move" (map plan B5): when the toggle is off, moves
          // only arm the "Search this area" pill instead of refetching -
          // user control AND fewer bursts at the small backend. The force
          // ref lets programmatic moves (B2 auto-widen) refetch regardless.
          if (autoSearch || forceNextSearchRef.current) {
            forceNextSearchRef.current = false;
            dispatchAreaSearch(leafletMap);
          } else {
            setHasPendingArea(true);
          }
        }
      }
    }
    setIsFlyto(false);
  }, [dispatchAreaSearch, isFlyto, autoSearch]);

  // Map control handlers (plan B5).
  const toggleAutoSearch = () => {
    const next = !autoSearch;
    try {
      localStorage.setItem('mapAutoSearch', next ? 'on' : 'off');
    } catch {
      // Private mode - preference just won't persist.
    }
    setAutoSearch(next);
    // Turning auto-search back on with a pending area = search it now.
    if (next && hasPendingArea && mapRef.current) {
      dispatchAreaSearch(mapRef.current);
    }
  };

  const searchThisArea = () => {
    if (mapRef.current) {
      dispatchAreaSearch(mapRef.current);
    }
  };

  const goToMyLocation = () => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      setTimeout(() => setLocationDenied(false), 3000);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationDenied(false);
        mapRef.current?.setView(
          [position.coords.latitude, position.coords.longitude],
          CLICKED_ZOOM,
          { animate: true }
        );
      },
      () => {
        setLocationDenied(true);
        setTimeout(() => setLocationDenied(false), 3000);
      }
    );
  };

  // Memoize the marker React elements separately from the parent render.
  // Without this, any MapboxLe re-render (setIsFlyto still fires on pan)
  // produces fresh <Marker> elements, and react-leaflet-cluster reacts by
  // removing/re-adding standalone markers — the visible "blink" on individual
  // storefront/personal/wanted pins. Clustered markers don't blink because
  // the cluster group manages them internally and only the cluster icon is
  // in the DOM. (setViewport/setLastBounds churn was removed in plan A4.)
  // Bottom-sheet pin cards (map plan F2): on the full-screen mobile map,
  // Leaflet popups are cramped - pin taps open a fixed bottom sheet instead.
  // sheetData holds the tapped pin's payload; the selected-pin styling is
  // applied manually since there's no popupopen/popupclose in sheet mode.
  const [sheetData, setSheetData] = useState<any | null>(null);
  const selectedPinRef = useRef<any>(null);
  const clearSelectedPin = () => {
    selectedPinRef.current
      ?.getElement()
      ?.querySelector('.dl-pin')
      ?.classList.remove('dl-pin-selected');
    selectedPinRef.current?.setZIndexOffset?.(0);
    selectedPinRef.current = null;
  };
  const closeSheet = useCallback(() => {
    setSheetData(null);
    selectedPinRef.current
      ?.getElement()
      ?.querySelector('.dl-pin')
      ?.classList.remove('dl-pin-selected');
    selectedPinRef.current?.setZIndexOffset?.(0);
    selectedPinRef.current = null;
  }, []);

  // F4 hover sync: card hover lights the matching pin WITHOUT rebuilding the
  // memoized marker array (rebuilding per hover would resurrect the pin-blink
  // bug). Markers register themselves in a ref map keyed by 'type-id'; an
  // effect toggles a class on the inner .dl-pin element directly.
  const markerByKeyRef = useRef<Map<string, any>>(new Map());
  const hoveredListingKey = useAppSelector((state) => state.filter.hoveredListing?.key ?? null);
  const hoveredListingSource = useAppSelector(
    (state) => state.filter.hoveredListing?.source ?? null
  );
  const prevHoverMarkerRef = useRef<any>(null);
  useEffect(() => {
    prevHoverMarkerRef.current
      ?.getElement?.()
      ?.querySelector('.dl-pin')
      ?.classList.remove('dl-pin-hovered');
    prevHoverMarkerRef.current?.setZIndexOffset?.(0);
    prevHoverMarkerRef.current = null;
    if (hoveredListingKey && hoveredListingSource === 'list') {
      const marker = markerByKeyRef.current.get(hoveredListingKey);
      if (marker) {
        marker.getElement?.()?.querySelector('.dl-pin')?.classList.add('dl-pin-hovered');
        marker.setZIndexOffset?.(900);
        prevHoverMarkerRef.current = marker;
      }
    }
  }, [hoveredListingKey, hoveredListingSource]);

  const markerChildren = useMemo(() => {
    markerByKeyRef.current = new Map();
    return points?.map((cluster: any) => {
      const [latitude, longitude] = cluster.geometry.coordinates;
      const type = cluster.properties?.data[0]?.type;
      // A lone storefront pin that is still an unclaimed community spot gets the
      // distinct community marker; claimed / owner-created shops keep the normal
      // storefront pin.
      const communitySpot = type === 'storefront' && isCommunitySpot(cluster.properties?.data?.[0]);
      return (
        <Marker
          ref={(marker: any) => {
            if (marker) {
              (cluster.properties?.data ?? []).forEach((entry: any) => {
                markerByKeyRef.current.set(`${entry?.type}-${entry?.id}`, marker);
              });
            }
          }}
          icon={
            type === 'wanted'
              ? needIcon
              : type === 'personal'
              ? listingIcon
              : type === 'event'
              ? eventMarkerIcon
              : communitySpot
              ? communityIcon
              : storeIcon
          }
          // The first entry's identity is part of the key: same-coordinate
          // stacks are now split per owner upstream, so coordinates alone no
          // longer uniquely name a marker.
          key={`${latitude},${longitude}:${cluster.properties?.data?.[0]?.type ?? ''}-${
            cluster.properties?.data?.[0]?.id ?? ''
          }`}
          position={[latitude, longitude]}
          // F6: markers are tab-reachable by Leaflet default; title gives
          // keyboard/screen-reader users a name for each pin.
          title={cluster.properties?.data?.[0]?.name ?? ''}
          eventHandlers={{
            // Selected state (E2): the open-popup pin scales up and rides
            // above its neighbors. Leaflet keeps at most one popup open, so
            // no bookkeeping is needed. In sheet mode (F2) the click handler
            // does the same bookkeeping by hand.
            click: (e: any) => {
              if (sheetMode) {
                clearSelectedPin();
                e.target.getElement()?.querySelector('.dl-pin')?.classList.add('dl-pin-selected');
                e.target.setZIndexOffset(1000);
                selectedPinRef.current = e.target;
                setSheetData({ type, data: cluster?.properties?.data ?? [] });
              }
            },
            popupopen: (e: any) => {
              e.target.getElement()?.querySelector('.dl-pin')?.classList.add('dl-pin-selected');
              e.target.setZIndexOffset(1000);
            },
            popupclose: (e: any) => {
              e.target.getElement()?.querySelector('.dl-pin')?.classList.remove('dl-pin-selected');
              e.target.setZIndexOffset(0);
            },
            // F4: pin hover rings + scrolls the matching list card.
            mouseover: () => {
              if (canHover) {
                dispatch(
                  setHoveredListing({
                    key: `${type}-${cluster.properties?.data?.[0]?.id}`,
                    source: 'map'
                  })
                );
              }
            },
            mouseout: () => {
              if (canHover) {
                dispatch(setHoveredListing(null));
              }
            }
          }}>
          {!sheetMode && (
            <Popup
              maxWidth={isMobile ? 240 : 300}
              autoPanPaddingTopLeft={L.point(56, 96)}
              autoPanPaddingBottomRight={L.point(56, 24)}>
              {/* Shop-first popup (map plan D3 / MAP_DISCOVERY_PLAN slice 2):
                  a lone storefront pin gets the shop card (logo, rating, item
                  thumbnails). A multi-item stack is one seller since the
                  owner split, so it gets the seller-first card with item
                  rows (map polish round 2) instead of the swipe slider. */}
              {type === 'storefront' && cluster?.properties?.data?.length === 1 ? (
                <div className="popup-shadow rounded-xl">
                  <ShopPopupCard storefront={cluster.properties.data[0]} />
                </div>
              ) : cluster?.properties?.data?.length > 1 ? (
                <div className="popup-shadow rounded-xl">
                  <SellerStackPopup listings={cluster.properties.data} />
                </div>
              ) : (
                cluster?.properties?.data?.length > 0 && (
                  <PopupSlider listings={cluster.properties.data} />
                )
              )}
            </Popup>
          )}
        </Marker>
      );
    });
    // eslint-disable-next-line
  }, [points, isMobile, sheetMode]);

  // Online Continent (Antarctica) markers: online-only shops as tailless globe
  // pins at deterministic Antarctic positions. Rendered outside the cluster
  // groups so they scatter instead of collapsing into one dot.
  const onlineMarkers = useMemo(() => {
    return (onlineStorefronts || []).map((s: any) => (
      <Marker
        key={`online-${s.id}`}
        position={onlineShopLatLng(s.id)}
        icon={onlineIcon}
        eventHandlers={{
          // Mobile parity: online pins previously had only the desktop <Popup>
          // and no sheet handler, so tapping one in sheet mode did nothing.
          // Mirror the listing/storefront markers - open the bottom sheet with
          // this shop's card.
          click: (e: any) => {
            if (sheetMode) {
              clearSelectedPin();
              e.target.getElement()?.querySelector('.dl-pin')?.classList.add('dl-pin-selected');
              e.target.setZIndexOffset(1000);
              selectedPinRef.current = e.target;
              setSheetData({ type: 'storefront', data: [s] });
            }
          },
          popupopen: (e: any) => {
            e.target.getElement()?.querySelector('.dl-pin')?.classList.add('dl-pin-selected');
            e.target.setZIndexOffset(1000);
          },
          popupclose: (e: any) => {
            e.target.getElement()?.querySelector('.dl-pin')?.classList.remove('dl-pin-selected');
            e.target.setZIndexOffset(0);
          }
        }}>
        {!sheetMode && (
          <Popup
            maxWidth={isMobile ? 240 : 300}
            autoPanPaddingTopLeft={L.point(56, 96)}
            autoPanPaddingBottomRight={L.point(56, 24)}>
            <div className="popup-shadow rounded-xl">
              <ShopPopupCard storefront={s} />
            </div>
          </Popup>
        )}
      </Marker>
    ));
    // eslint-disable-next-line
  }, [onlineStorefronts, isMobile, sheetMode]);

  // Opted-in users rendered as a separate clustered layer so the existing
  // listing markers are untouched. Memoized to avoid marker churn on pan.
  const openUserProfile = useCallback(
    (id: number) => {
      dispatch(setProfileData({ userId: id }));
      dispatch(showSlide('profile'));
    },
    [dispatch]
  );
  // Perf (map plan A4): useGetUsersOnMap already returns a referentially
  // stable array (Apollo result caching + previousData fallback), so the old
  // stringify-the-whole-list-on-every-render identity trick is pure cost.
  const userMarkers = useMemo(() => {
    return (users || []).map((user: any) => {
      const founder = getFounderConfig(user?.id);
      return (
        <Marker
          icon={createUserIcon(user)}
          key={`user-${user?.id}`}
          position={[user?.latitude, user?.longitude]}>
          <Popup
            maxWidth={240}
            autoPanPaddingTopLeft={L.point(56, 96)}
            autoPanPaddingBottomRight={L.point(56, 24)}>
            <div
              onClick={() => openUserProfile(Number(user?.id))}
              className="cursor-pointer rounded-lg border border-gray-200 bg-white p-3">
              <div className="flex items-center gap-2">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <span className="font-semibold text-yellow-800">
                    {(user?.name || user?.username || '?').charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="font-semibold text-gray-900">{user?.name || user?.username}</span>
              </div>
              <div className="mt-1 text-xs text-yellow-800">
                <FormattedMessage
                  id="page.marketplace.user_popup.open_to_trade"
                  defaultMessage="Open to trade in DOGE"
                />
              </div>
              {founder ? <div className="mt-1 text-xs text-gray-600">{founder.label}</div> : null}
              <div className="mt-2 text-xs font-medium text-blue-600">
                <FormattedMessage
                  id="page.marketplace.user_popup.view_profile"
                  defaultMessage="View profile"
                />
              </div>
            </div>
          </Popup>
        </Marker>
      );
    });
  }, [users, openUserProfile]);

  return (
    // F1 layout hygiene: w-[100vh] (viewport HEIGHT as width!) and the pe-10
    // fudge are gone; the parent now owns the size. lg:h-full fills the
    // sticky desktop wrapper; band layouts size via the height prop.
    <div className={clsx('relative w-full lg:h-full', className)}>
      {/* Thin refresh indicator: markers stay mounted through fetches (no more
          full pin blink), so this bar is the only "loading" signal. Sits above
          the MapContainer (z-0 stacking context) and never blocks input. */}
      {loading && (
        <div className="pointer-events-none absolute top-0 left-0 right-0 h-[3px] z-[3] overflow-hidden">
          <div className="map-loading-bar" />
        </div>
      )}
      {/* Floating keyword bar (F7 round 4): the ONLY keyword entry in the
          full-screen mobile map view (the Filters block is css-hidden there).
          type=search + form submit puts a Search key on mobile keyboards;
          text-base (16px) stops iOS from zooming the page on focus. */}
      {sheetMode && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            applyMapSearch(mapSearchText.trim());
            // Drop the keyboard so the refreshed pins are actually visible.
            (document.activeElement as HTMLElement | null)?.blur?.();
          }}
          className="absolute inset-x-3 top-3 z-[3]">
          <div className="flex h-11 items-center gap-2 rounded-full border border-gray-200 bg-white ps-4 pe-1.5 shadow-md">
            <SearchIcon className="h-5 w-5 shrink-0 text-gray-500" />
            <input
              type="search"
              value={mapSearchText}
              onChange={(event) => setMapSearchText(event.target.value)}
              placeholder={intl.formatMessage({
                id: TRANSLATED_CONSTANTS.SEARCH_BY_ITEM_NAME.id,
                defaultMessage: TRANSLATED_CONSTANTS.SEARCH_BY_ITEM_NAME.defaultMessage
              })}
              aria-label={intl.formatMessage({
                id: TRANSLATED_CONSTANTS.SEARCH_BY_ITEM_NAME.id,
                defaultMessage: TRANSLATED_CONSTANTS.SEARCH_BY_ITEM_NAME.defaultMessage
              })}
              className="map-search-input h-full w-full min-w-0 flex-1 bg-transparent text-base text-gray-900 placeholder:text-gray-500 focus:outline-none"
            />
            {Boolean(mapSearchText || filterFromModal?.searchText) && (
              <button
                type="button"
                onClick={() => {
                  setMapSearchText('');
                  if (filterFromModal?.searchText) {
                    applyMapSearch('');
                  }
                }}
                aria-label={intl.formatMessage({
                  id: TRANSLATED_CONSTANTS.CLEAR.id,
                  defaultMessage: TRANSLATED_CONSTANTS.CLEAR.defaultMessage
                })}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-lg leading-none text-gray-500 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500">
                ×
              </button>
            )}
          </div>
        </form>
      )}
      {/* Category discovery P0: the chip bar's map placement - the row under
          the keyword bar (keyword owns top-3, chips own top-[60px]). Only the
          chips catch the pointer, so the map stays draggable around them; the
          toggle / search-this-area / zoom controls below all drop by the
          bar's 54px reserved height. Desktop (non-sheet) maps carry NO bar -
          the list column's sticky bar is the one category control at lg+. */}
      {sheetMode && <CategoryChipBar placement="map" />}
      {/* Map controls (plan B5). Discrete absolute elements so the map stays
          fully draggable everywhere else. Overlay z values are SINGLE DIGITS
          on purpose: the MapContainer is its own z-0 stacking context, so
          z-[3] already clears every leaflet pane - and staying under z-10
          keeps the portaled site layers (message/profile slide z-10, command
          menus z-20, modals z-30) painting ABOVE the map UI. The old
          500/600 values hid the message drawer behind the pin sheet
          (Luke's F7 round 3 bug). */}
      {/* sheetMode drops the toggle TWO rows: the keyword bar owns top-3 and
          the category chip bar owns top-[60px] (category discovery P0). */}
      <label
        className={clsx(
          sheetMode ? 'top-[120px]' : 'top-3',
          'absolute left-3 z-[3] flex cursor-pointer select-none items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 shadow-md'
        )}>
        <input
          type="checkbox"
          checked={autoSearch}
          onChange={toggleAutoSearch}
          className="h-4 w-4 accent-yellow-500"
        />
        <span className="text-sm font-medium text-gray-700">
          <FormattedMessage
            id={TRANSLATED_CONSTANTS.SEARCH_AS_I_MOVE.id}
            defaultMessage={TRANSLATED_CONSTANTS.SEARCH_AS_I_MOVE.defaultMessage}
          />
        </span>
      </label>
      {hasPendingArea && !autoSearch && (
        <button
          type="button"
          onClick={searchThisArea}
          className={clsx(
            sheetMode ? 'top-[172px]' : 'top-16',
            'absolute left-1/2 z-[3] -translate-x-1/2 rounded-full bg-yellow-400 px-4 py-2 text-sm font-semibold text-gray-900 shadow-md hover:bg-yellow-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900'
          )}>
          <FormattedMessage
            id={TRANSLATED_CONSTANTS.SEARCH_THIS_AREA.id}
            defaultMessage={TRANSLATED_CONSTANTS.SEARCH_THIS_AREA.defaultMessage}
          />
        </button>
      )}
      {points.length >= MAP_MARKER_LIMIT && (
        <div className="pointer-events-none absolute bottom-6 left-3 z-[3] rounded-full border border-gray-200 bg-white/95 px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm">
          <FormattedMessage
            id={TRANSLATED_CONSTANTS.MAP_CAP_NOTE.id}
            defaultMessage={TRANSLATED_CONSTANTS.MAP_CAP_NOTE.defaultMessage}
          />
        </div>
      )}
      <button
        type="button"
        onClick={goToMyLocation}
        aria-label={intl.formatMessage({
          id: TRANSLATED_CONSTANTS.MY_LOCATION.id,
          defaultMessage: TRANSLATED_CONSTANTS.MY_LOCATION.defaultMessage
        })}
        className="absolute bottom-10 right-3 z-[3] flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white shadow-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500">
        <MapNavigationPointIcon className="h-5 w-5 text-gray-700" />
      </button>
      {locationDenied && (
        <div className="pointer-events-none absolute bottom-24 right-3 z-[3] rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm">
          <FormattedMessage
            id={TRANSLATED_CONSTANTS.LOCATION_UNAVAILABLE.id}
            defaultMessage={TRANSLATED_CONSTANTS.LOCATION_UNAVAILABLE.defaultMessage}
          />
        </div>
      )}
      {/* Seed the Map momentum chip (plan section 4). Hidden while the mobile
          bottom sheet is open (it would sit underneath it); lifts a row when
          the map-cap note occupies the bottom-left corner. */}
      {!sheetData && (
        <SpotMomentumChip lifted={points.length >= MAP_MARKER_LIMIT} sheet={sheetMode} />
      )}
      <MapContainer
        center={viewport.center}
        style={{ width: width, height: `${height ?? '100%'}` }}
        zoom={viewport.zoom}
        minZoom={BASE_MIN_ZOOM}
        maxBounds={WORLD_BOUNDS}
        maxBoundsViscosity={1.0}
        ref={mapRef}
        className={clsx('z-0', sheetMode && 'map-sheet-controls')}
        scrollWheelZoom={true}
        fadeAnimation={true}
        zoomAnimation={true}
        zoomControl={false}>
        <ZoomControl position="topright" />
        {/* Attribution is a license requirement for Mapbox/OSM tiles (it was
            stripped to "" before - map plan E1). buildTileUrl inserts the
            z/x/y placeholders so the env var can stay brace-free (DO's env
            editor rejects braces). bounds stops tile requests outside the
            one world (they came back as stray repeated strips). */}
        <TileLayer
          attribution='&copy; <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noreferrer">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> <a href="https://apps.mapbox.com/feedback/" target="_blank" rel="noreferrer">Improve this map</a>'
          noWrap={true}
          bounds={WORLD_BOUNDS}
          url={buildTileUrl(process.env.REACT_APP_MAPBOX_TILE, theme === 'dark')}
        />
        <MapCleanup />
        <FitWorldMinZoom />
        {/* Wheel zoom: desktop split view, and the F2 full-screen mobile map
            (nothing behind it to scroll). Band-era hijacking stays dead. */}
        <WheelZoomGuard enabled={isDesktopViewport || sheetMode} />
        {sheetMode && sheetData && <SheetMapClickClose onMapClick={closeSheet} />}
        {/* Always mounted: gating these on !loading unmounted every marker and
            the moveend listener on EACH fetch (the "all pins blink" bug). The
            hooks keep their previous data while a request is in flight, so the
            old markers remain correct until fresh ones replace them. */}
        {/* Cluster tuning (map plan D2): tighter radius so city-level views
            resolve to pins sooner, spiderfy at max zoom for near-neighbors,
            and no clustering at street zoom. Upstream grouping merges one
            SELLER's same-coordinate items into one marker with the card
            slider; different sellers at one stored coordinate arrive as
            separate ring-offset markers, so here they cluster with an honest
            numbered badge instead of silently merging into one pin. */}
        {/* Online Continent zone (C3b): the Antarctic band tinted + dashed, with
            a label + legend pill so the pins read as a place, not ocean noise. */}
        <Rectangle
          bounds={ANTARCTICA_BOUNDS}
          pathOptions={{
            interactive: false,
            color: theme === 'dark' ? '#4F6E80' : '#8FB3C7',
            weight: 2,
            dashArray: '8 6',
            fillColor: theme === 'dark' ? '#22323C' : '#EAF4F9',
            fillOpacity: 0.45
          }}
        />
        <Marker position={ANTARCTICA_LABEL_POS} icon={continentLabelIcon} interactive={false} />
        {/* Online Continent: online-only shops as tailless globe pins, scattered
            across Antarctica (deterministic per id). Not clustered. */}
        {onlineMarkers}
        <MarkerClusterGroup
          showCoverageOnHover={false}
          iconCreateFunction={createClusterCustomIcon}
          removeOutsideVisibleBounds={false}
          maxClusterRadius={50}
          spiderfyOnMaxZoom={true}
          zoomToBoundsOnClick={true}
          disableClusteringAtZoom={15}
          chunkedLoading>
          {markerChildren}
        </MarkerClusterGroup>
        <MarkerClusterGroup
          showCoverageOnHover={false}
          iconCreateFunction={createUserClusterIcon}
          removeOutsideVisibleBounds={false}
          maxClusterRadius={50}
          spiderfyOnMaxZoom={true}
          zoomToBoundsOnClick={true}
          disableClusteringAtZoom={15}
          chunkedLoading>
          {userMarkers}
        </MarkerClusterGroup>
        <MapEventComponent
          handleMapMove={handleMapMove}
          hasSyncedInitialBounds={hasSyncedInitialBounds}
        />
      </MapContainer>
      {/* F2 bottom sheet: the mobile stand-in for pin popups. Same cards as
          the desktop popups (shop card for lone shops, slider otherwise).
          Closes via the X or any tap on the map. px-12 keeps the slider's
          outboard arrows inside the sheet. */}
      {sheetMode && sheetData && (
        // max-h + inner scroll (F7 round 2): tall cards (personal/event with
        // big images) were clipping at the sheet bottom on short phones -
        // now anything oversized scrolls INSIDE the sheet instead.
        // z-[6] (F7 round 3): above the map controls (z-[3]) and the view
        // pill (z-[5] in index.tsx), but BELOW the portaled message slide
        // (z-10) and modals (z-30) so Message Seller opens on top.
        <div className="absolute inset-x-2 bottom-[calc(8px+env(safe-area-inset-bottom))] z-[6] max-h-[calc(100%-24px)] overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-xl">
          <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-gray-300" />
          <button
            type="button"
            onClick={closeSheet}
            aria-label={intl.formatMessage({
              id: TRANSLATED_CONSTANTS.CLOSE_SHEET.id,
              defaultMessage: TRANSLATED_CONSTANTS.CLOSE_SHEET.defaultMessage
            })}
            className="absolute right-2 top-2 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-xl leading-none text-gray-600 shadow-sm hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500">
            ×
          </button>
          {/* Sheet content (F7 polish): purpose-built cards instead of the
              desktop popup card - compact, full-width, never scroll-clipped.
              A multi-item stack is one seller since the owner split, so it
              gets the same seller-first card with item rows as the desktop
              popup (map polish round 2) - the swipe carousel is gone. */}
          {sheetData.type === 'storefront' && sheetData.data?.length === 1 ? (
            <div className="pt-1">
              <ShopPopupCard storefront={sheetData.data[0]} sheet={true} />
            </div>
          ) : sheetData.data?.length === 1 ? (
            <div className="pt-1">
              <SheetListingCard listing={sheetData.data[0]} />
            </div>
          ) : (
            sheetData.data?.length > 0 && (
              <div className="pt-1 pb-1">
                <SellerStackPopup listings={sheetData.data} sheet={true} />
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default MapboxLe;
